import React from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/react-hooks';

import { FIND_SHOWS } from 'graphql/show';
import { DashboardRouteParams, ShowDb } from 'types';

type IShowsContext = {
  loading: boolean;
  error?: string;
  shows: ShowDb[];
  selectedShow: ShowDb;
  selectedShowRss?: string;
  setSelectedShow: (selected: ShowDb) => void;
};

const SHOW_KEY = 'podlink/show';

export const ShowsContext = React.createContext<IShowsContext>({} as IShowsContext);

const ShowsProvider: React.FC = ({ children }) => {
  const { vanityUrl } = useParams<DashboardRouteParams>();
  const [savedShow] = React.useState(() => {
    const jsonShow = localStorage.getItem(SHOW_KEY);
    return jsonShow ? JSON.parse(jsonShow) : ({} as ShowDb);
  });
  const [selectedShow, _setSelectedShow] = React.useState({} as ShowDb);
  const [selectedShowRss, setSelectedShowRss] = React.useState<string | undefined>();
  const [shows, setShows] = React.useState([] as ShowDb[]);
  const [error, setError] = React.useState('');
  const { loading, error: apolloError, data } = useQuery(FIND_SHOWS);

  const setSelectedShow = (selected: ShowDb) => {
    _setSelectedShow(selected);
    setSelectedShowRss(getRssLink(selected));
    const partialShow = {
      itunesId: selected.itunesId,
      title: selected.title,
      vanityUrl: selected.vanityUrl,
    };
    localStorage.setItem(SHOW_KEY, JSON.stringify(partialShow));
  };

  const getRssLink = (selected: ShowDb): string | undefined =>
    selected && selected.links ? selected.links!.find((l) => l?.slug === 'rss')?.link || '' : '';

  const setInitalSelected = (s: ShowDb[]) => {
    let showIdx = 0;
    if (vanityUrl) {
      showIdx = s.findIndex((show) => show.vanityUrl === vanityUrl);
    } else if (savedShow && savedShow.vanityUrl) {
      showIdx = s.findIndex((show) => show.vanityUrl === savedShow.vanityUrl);
    }
    const selected = showIdx === -1 ? s[0] : s[showIdx];
    _setSelectedShow(selected);
    setSelectedShowRss(getRssLink(selected));
  };

  React.useEffect(() => {
    if (!loading && data?.shows) {
      setShows(data.shows);
      setInitalSelected(data.shows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data]);

  React.useEffect(() => {
    apolloError && apolloError?.message && setError(apolloError.message);
  }, [apolloError]);

  return (
    <ShowsContext.Provider
      value={{
        loading,
        error,
        shows,
        selectedShow,
        selectedShowRss,
        setSelectedShow,
      }}
    >
      {children}
    </ShowsContext.Provider>
  );
};

export default ShowsProvider;
